import { z } from 'zod';
import { LIMITS } from './limits.js';
import { MetricSchema } from './metrics.js';
import { NumberFormatSchema, DEFAULT_NUMBER_FORMAT } from './format.js';
import { ReportPadError } from './errors.js';
/** Bump when the persisted report definition shape changes incompatibly. */
export const REPORT_SCHEMA_VERSION = 1;
const widgetId = z.string().min(1).max(64);
const label = z.string().min(1).max(LIMITS.maxLabelChars);
const columnRef = z.string().min(1).max(64);
// ---------------------------------------------------------------------------
// Widgets
// ---------------------------------------------------------------------------
export const WIDGET_TYPES = ['kpi', 'chart', 'table', 'text'];
const widgetBase = {
    id: widgetId,
    title: label
};
export const KpiWidgetSchema = z.object({
    ...widgetBase,
    type: z.literal('kpi'),
    metric: MetricSchema,
    format: NumberFormatSchema.default(DEFAULT_NUMBER_FORMAT),
    /** Short line under the value, e.g. "across all jobs". Author-written. */
    caption: z.string().max(200).optional()
});
export const ChartSeriesSchema = z.object({
    id: z.string().min(1).max(64),
    label,
    metric: MetricSchema
});
export const ChartWidgetSchema = z.object({
    ...widgetBase,
    type: z.literal('chart'),
    chartType: z.enum(['bar', 'line']),
    /** Column whose distinct values become the category axis. */
    dimensionColumnId: columnRef,
    series: z.array(ChartSeriesSchema).min(1).max(6),
    format: NumberFormatSchema.default(DEFAULT_NUMBER_FORMAT),
    /** Cap on categories drawn; the rest are dropped with a visible note. */
    maxCategories: z.number().int().min(2).max(60).default(24),
    stacked: z.boolean().default(false)
});
/** How a column's subtotal is computed on a group row.
 *  Reuses the frozen Metric type; `none` means "deliberately blank". */
export const ColumnRollupSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('none') }),
    ...MetricSchema.options
]);
export const TableColumnSchema = z.object({
    columnId: columnRef,
    /** Overrides the dataset column label when set. */
    label: label.optional(),
    format: NumberFormatSchema.optional(),
    /** Absent means inferred: `sum` for numeric columns, blank otherwise. */
    rollup: ColumnRollupSchema.optional()
});
export const TableWidgetSchema = z.object({
    ...widgetBase,
    type: z.literal('table'),
    columns: z.array(TableColumnSchema).min(1).max(LIMITS.maxDatasetColumns),
    sort: z
        .object({ columnId: columnRef, direction: z.enum(['asc', 'desc']).default('asc') })
        .optional(),
    /** Rows shown on screen. The PDF flows the full table regardless. */
    pageSize: z.number().int().min(5).max(500).default(25),
    /** Columns to group by, outermost first. Absent means a flat table. */
    groupBy: z.array(columnRef).min(1).max(3).optional(),
    /** Depth expanded on first render and in the PDF. Clamped to groupBy.length
     *  once the table is grouped; left as authored while groupBy is absent, so
     *  a later patch that only adds groupBy does not switch grouping on fully
     *  collapsed. */
    defaultExpandedDepth: z.number().int().min(0).default(1)
});
export const TextWidgetSchema = z.object({
    ...widgetBase,
    type: z.literal('text'),
    /** Plain text only. Never rendered as HTML or Markdown — this string comes
     *  from an agent or an upload and is untrusted. */
    body: z.string().max(LIMITS.maxTextWidgetChars),
    /** Marks the block as human/agent-authored commentary rather than a computed
     *  figure. Rendered with a visible "Note" affordance so a reader can tell
     *  narrative from calculation, especially after a data refresh. */
    authored: z.literal(true).default(true)
});
export const WidgetSchema = z.discriminatedUnion('type', [
    KpiWidgetSchema,
    ChartWidgetSchema,
    TableWidgetSchema,
    TextWidgetSchema
]);
// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------
/** GridStack coordinates. Screen-only: the PDF never uses these. */
export const GRID_COLUMNS = 12;
export const LayoutItemSchema = z.object({
    widgetId,
    x: z.number().int().min(0).max(GRID_COLUMNS - 1),
    y: z.number().int().min(0).max(1000),
    w: z.number().int().min(1).max(GRID_COLUMNS),
    h: z.number().int().min(1).max(60)
});
export const LayoutSchema = z.object({
    columns: z.literal(GRID_COLUMNS).default(GRID_COLUMNS),
    items: z.array(LayoutItemSchema).max(LIMITS.maxWidgetsPerReport)
});
// ---------------------------------------------------------------------------
// Print
// ---------------------------------------------------------------------------
/** Print order and options are stored separately from the dashboard grid.
 *  A printed page flows; it does not reproduce absolute grid coordinates. */
export const PrintOptionsSchema = z.object({
    pageSize: z.enum(['Letter', 'A4']).default('Letter'),
    orientation: z.enum(['portrait', 'landscape']).default('portrait'),
    /** Widget ids in print order. Widgets absent from this list print after the
     *  listed ones, in definition order, so a new widget is never silently
     *  dropped from the PDF. */
    order: z.array(widgetId).max(LIMITS.maxWidgetsPerReport).default([]),
    /** Widget ids deliberately excluded from the PDF. */
    hidden: z.array(widgetId).max(LIMITS.maxWidgetsPerReport).default([]),
    includeCoverSummary: z.boolean().default(true),
    includeSourceNotes: z.boolean().default(true)
});
export const DEFAULT_PRINT_OPTIONS = {
    pageSize: 'Letter',
    orientation: 'portrait',
    order: [],
    hidden: [],
    includeCoverSummary: true,
    includeSourceNotes: true
};
// ---------------------------------------------------------------------------
// Report definition
// ---------------------------------------------------------------------------
/**
 * Everything a human can edit. Note what is *not* here: no snapshot id, no row
 * data, no timestamps. Those belong to the report record, which is updated by a
 * different operation with different concurrency rules.
 */
export const ReportDefinitionSchema = z.object({
    schemaVersion: z.literal(REPORT_SCHEMA_VERSION).default(REPORT_SCHEMA_VERSION),
    title: label,
    /** Short line under the title, e.g. "Six active jobs". Author-written. */
    subtitle: z.string().max(LIMITS.maxSubtitleChars).optional(),
    /** Recipient line on the report's cover, e.g. "Meridian Builders". */
    preparedFor: z.string().max(LIMITS.maxPreparedForChars).optional(),
    widgets: z.array(WidgetSchema).max(LIMITS.maxWidgetsPerReport),
    layout: LayoutSchema,
    print: PrintOptionsSchema.default(DEFAULT_PRINT_OPTIONS)
});
/** The stored report: definition plus the pointers and bookkeeping the server
 *  owns. `revision` advances on definition edits only; `activeSnapshotId` moves
 *  on refresh only. Keeping them apart is what makes "refresh preserves edits"
 *  true by construction rather than by care. */
export const ReportSchema = z.object({
    id: z.string().min(1),
    datasetId: z.string().min(1),
    activeSnapshotId: z.string().min(1).nullable(),
    revision: z.number().int().positive(),
    definition: ReportDefinitionSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
    /** When the active snapshot last changed. Null until the first refresh. */
    dataRefreshedAt: z.string().nullable()
});
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
export function findWidget(definition, widgetId_) {
    return definition.widgets.find((w) => w.id === widgetId_);
}
/** Every dataset column referenced anywhere in a widget. */
export function widgetColumnIds(widget) {
    switch (widget.type) {
        case 'kpi':
            return metricColumns(widget.metric);
        case 'chart':
            return [widget.dimensionColumnId, ...widget.series.flatMap((s) => metricColumns(s.metric))];
        case 'table':
            return [
                ...widget.columns.map((c) => c.columnId),
                ...widget.columns.flatMap((c) => (c.rollup && c.rollup.kind !== 'none' ? metricColumns(c.rollup) : [])),
                ...(widget.groupBy ?? []),
                ...(widget.sort ? [widget.sort.columnId] : [])
            ];
        case 'text':
            return [];
    }
}
function metricColumns(metric) {
    switch (metric.kind) {
        case 'sum':
        case 'average':
            return [metric.columnId];
        case 'count':
            return metric.columnId ? [metric.columnId] : [];
        case 'ratio_of_sums':
            return [metric.numeratorColumnId, metric.denominatorColumnId];
    }
}
/** Every column the whole definition depends on. This is the set that a new
 *  snapshot must still satisfy for a refresh to be accepted. */
export function definitionColumnIds(definition) {
    return [...new Set(definition.widgets.flatMap(widgetColumnIds))];
}
/** Resolves the print sequence: explicit order first, then any unlisted
 *  widgets in definition order, minus hidden ones. */
export function resolvePrintOrder(definition) {
    const hidden = new Set(definition.print.hidden);
    const byId = new Map(definition.widgets.map((w) => [w.id, w]));
    const out = [];
    const seen = new Set();
    for (const id of definition.print.order) {
        const widget = byId.get(id);
        if (widget && !hidden.has(id) && !seen.has(id)) {
            out.push(widget);
            seen.add(id);
        }
    }
    for (const widget of definition.widgets) {
        if (!seen.has(widget.id) && !hidden.has(widget.id))
            out.push(widget);
    }
    return out;
}
/** Default grid box for a newly added widget, by type. */
export function defaultLayoutSize(type) {
    switch (type) {
        case 'kpi':
            return { w: 3, h: 3 };
        case 'chart':
            return { w: 6, h: 8 };
        case 'table':
            return { w: 12, h: 10 };
        case 'text':
            return { w: 6, h: 4 };
    }
}
/** Finds the first free row below everything currently placed. */
export function nextFreeRow(layout) {
    return layout.items.reduce((max, item) => Math.max(max, item.y + item.h), 0);
}
function overlaps(a, b) {
    return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}
function cells(item) {
    return `x ${item.x}, y ${item.y}, w ${item.w}, h ${item.h}`;
}
/** Refuses any overlapping pair in `after` that `before` did not already have. */
export function assertNoNewOverlaps(before, after) {
    const was = before?.layout.items ?? [];
    const old = (a, b) => {
        const pa = was.find((i) => i.widgetId === a);
        const pb = was.find((i) => i.widgetId === b);
        return pa !== undefined && pb !== undefined && overlaps(pa, pb);
    };
    const issues = [];
    const items = after.layout.items;
    for (let i = 0; i < items.length; i++) {
        for (let j = i + 1; j < items.length; j++) {
            const [a, b] = [items[i], items[j]];
            if (!overlaps(a, b) || old(a.widgetId, b.widgetId))
                continue;
            issues.push({
                code: 'LAYOUT_INVALID',
                message: `Widget ${a.widgetId} (${cells(a)}) would overlap ${b.widgetId} (${cells(b)}).`,
                location: { widgetId: a.widgetId }
            });
        }
    }
    if (issues.length > 0) {
        throw new ReportPadError('LAYOUT_INVALID', issues[0].message, { issues });
    }
}
//# sourceMappingURL=report.js.map